import { useState } from "react";
import { Plus, Trash2, Loader2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { StudentPhotoPicker } from "@/components/StudentPhotoPicker";
import { AddressInput } from "@/components/AddressInput";
import { PhoneInput } from "@/components/PhoneInput";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const emptyPickup = { name: "", license_number: "", relationship: "parent", phone: "", email: "" };

export const emptyStudent = { full_name: "", student_number: "", grade: "", class_id: "", class_ids: [], car_number: "", rider_type: "car", bus_id: "", photo_data_url: "", date_of_birth: "", address: "", address_verified: false, authorized_pickups: [emptyPickup] };

export const studentToForm = (s) => ({
  full_name: s.full_name, student_number: s.student_number || "", grade: s.grade || "", class_id: s.class_id || "", class_ids: s.class_ids || [], car_number: s.car_number || "", rider_type: s.rider_type || "car", bus_id: s.bus_id || "", photo_data_url: s.photo_data_url || "", date_of_birth: s.date_of_birth || "", address: s.address || "", address_verified: !!s.address_verified,
  authorized_pickups: s.authorized_pickups?.length ? s.authorized_pickups : [emptyPickup],
});

// student: "new" | student doc. onSaved(createdOrNull, form) fires after a successful save.
export function StudentEditorDialog({ student, classes, buses, onClose, onSaved }) {
  const isNew = student === "new";
  const [form, setForm] = useState(isNew ? emptyStudent : studentToForm(student));
  const [saving, setSaving] = useState(false);

  const addPickup = () => setForm((f) => ({ ...f, authorized_pickups: [...f.authorized_pickups, { ...emptyPickup, relationship: "" }] }));
  const removePickup = (idx) => setForm((f) => ({ ...f, authorized_pickups: f.authorized_pickups.filter((_, i) => i !== idx) }));
  const setPickup = (idx, key, value) => setForm((f) => ({ ...f, authorized_pickups: f.authorized_pickups.map((p, i) => (i === idx ? { ...p, [key]: value } : p)) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let created = null;
      if (isNew) {
        ({ data: created } = await api.post("/students", form));
        toast.success(`${form.full_name} added`);
      } else {
        await api.put(`/students/${student.id}`, form);
        toast.success(`${form.full_name} updated`);
      }
      onSaved?.(created, form);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center px-4 py-8 overflow-auto" data-testid="student-editor">
      <form onSubmit={save} className="bg-white rounded-2xl shadow-xl max-w-5xl w-full p-6 max-h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">{isNew ? "Add student" : "Edit student"}</h2>
          <button type="button" onClick={onClose} data-testid="student-editor-close" className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-600 text-white shadow-md shadow-red-600/30 hover:bg-red-700 transition-colors ml-4"><X className="h-5 w-5" strokeWidth={2.5} /></button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <StudentPhotoPicker value={form.photo_data_url} onChange={(v) => setForm({ ...form, photo_data_url: v })} testid="student-photo" />
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Student ID</span>
              <input value={form.student_number} onChange={(e) => setForm({ ...form, student_number: e.target.value })} placeholder={isNew ? "Auto-assigned" : ""} data-testid="student-number-input" className={`${inputCls} font-mono`} />
              <span className="block mt-1 text-[10px] text-slate-400">Assigned automatically — override to match another system (e.g. Infinite Campus).</span>
            </label>
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Date of birth</span>
              <input type="date" value={form.date_of_birth} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="student-dob-input" className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Home address</span>
              <AddressInput value={form.address} verified={form.address_verified} onChange={(v, ok) => setForm({ ...form, address: v, address_verified: ok })} testid="student-address-input" className={inputCls} />
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
          <label className="sm:col-span-2 block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Full name</span>
            <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="student-name-input" className={inputCls} />
          </label>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Homeroom course</span>
            <select value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} data-testid="student-grade-select" className={inputCls}>
              <option value="">No homeroom yet</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.teacher_name ? ` · ${c.teacher_name}` : ""}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Rider type</span>
            <select value={form.rider_type} onChange={(e) => setForm({ ...form, rider_type: e.target.value })} data-testid="student-rider-select" className={inputCls}>
              <option value="car">Car rider</option>
              <option value="bus">School bus rider</option>
            </select>
          </label>
          {form.rider_type === "bus" ? (
            <label className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Bus</span>
              <select value={form.bus_id} onChange={(e) => setForm({ ...form, bus_id: e.target.value })} data-testid="student-bus-select" className={inputCls}>
                <option value="">Choose a bus…</option>
                {buses.map((b) => <option key={b.id} value={b.id}>Bus {b.number}{b.driver_name ? ` · ${b.driver_name}` : ""}</option>)}
              </select>
            </label>
          ) : (
            <div className="block">
              <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Car rider #</span>
              <div data-testid="student-carnum-display" className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm flex items-center font-bold text-slate-700">
                {form.car_number || "Auto-assigned on save"}
              </div>
              <span className="block mt-1 text-[10px] text-slate-400">Numbers are generated automatically and can't be edited</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest text-slate-500">Authorized pickups</span>
          <button type="button" onClick={addPickup} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="add-pickup-btn"><Plus className="h-3 w-3" /> add another</button>
        </div>
        <div className="space-y-3">
          {form.authorized_pickups.map((ap, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.3fr_1fr_0.8fr_1fr_1.2fr_auto] gap-2 rounded-lg border border-slate-200 p-3 bg-slate-50">
              <input placeholder="Full name (matches license)" value={ap.name} onChange={(e) => setPickup(i, "name", e.target.value)} data-testid={`pickup-name-${i}`} className={inputCls} required={i === 0} />
              <input placeholder="DL / ID" value={ap.license_number} onChange={(e) => setPickup(i, "license_number", e.target.value)} data-testid={`pickup-license-${i}`} className={inputCls} />
              <input placeholder="Relationship" value={ap.relationship} onChange={(e) => setPickup(i, "relationship", e.target.value)} className={inputCls} />
              <PhoneInput placeholder="Phone" value={ap.phone} onChange={(v) => setPickup(i, "phone", v)} data-testid={`pickup-phone-${i}`} className={inputCls} />
              <input placeholder="Email (for alerts)" type="email" value={ap.email || ""} onChange={(e) => setPickup(i, "email", e.target.value)} data-testid={`pickup-email-${i}`} className={inputCls} />
              {form.authorized_pickups.length > 1 && (
                <button type="button" onClick={() => removePickup(i)} className="text-destructive hover:text-destructive/80" data-testid={`remove-pickup-${i}`}><Trash2 className="h-4 w-4" /></button>
              )}
              <div className="sm:col-span-6">
                <AddressInput value={ap.address || ""} verified={!!ap.address_verified} placeholder="Home address (optional)" testid={`pickup-address-${i}`} className={inputCls}
                  onChange={(v, ok) => setForm((f) => ({ ...f, authorized_pickups: f.authorized_pickups.map((x, j) => j === i ? { ...x, address: v, address_verified: ok } : x) }))} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
          <button type="submit" disabled={saving} data-testid="student-save-btn" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:shadow disabled:opacity-60 inline-flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}
